import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { MaskedLines } from '../fx/MaskedLines';
import GhostWord from '../fx/GhostWord';
import { SectionLabel, PADX } from '../home/ui';
import { EASE_EXPO } from '../fx/ease';
import { mission, vision } from '../../data/identity';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10%' }}
      transition={{ duration: 0.7, ease: EASE_EXPO, delay }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Vision & Mission の黒い帯（会社概要とホームで共用。2026-10-18 代表指示でホームにも同じものを置いた）。
 * 黒い背景は呼ぶ側（Section）で付ける。aboutLink を渡すと、最後に会社概要への導線を出す。
 * showEnglish={false} のときは、英語の大見出し（ADVANCE HUMAN HISTORY）を出さず「人類史を前進させる。」から始める
 * （ホームはすぐ上のヒーローに同じ英語があるため。2026-10-18 代表指示）。
 */
export default function VisionMissionBand({ no = '01', aboutLink = false, showEnglish = true }) {
  return (
    <div className={`${PADX} py-20 md:py-28`}>
      <SectionLabel no={no} dark>
        Vision &amp; Mission
      </SectionLabel>

      <div className="mt-10 text-[11px] font-semibold uppercase tracking-label text-live">Vision</div>
      {showEnglish ? (
        <>
          <MaskedLines
            as="h2"
            per="char"
            lines={vision.enLines}
            lineClassName={(i) => (i === 2 ? 'pl-[0.55em]' : '')}
            className="display-type mt-4 text-[13vw] text-paper sm:text-8xl lg:text-[7vw] 2xl:text-[112px]"
            stagger={0.1}
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-[8vw] font-black leading-[1.2] tracking-display-ja text-paper md:text-[3.8vw] 2xl:text-[58px]">
              {vision.jp}
            </p>
          </Reveal>
        </>
      ) : (
        <MaskedLines
          as="h2"
          lines={vision.jpLinesSplit}
          className="mt-4 text-[9vw] font-black leading-[1.2] tracking-display-ja text-paper md:text-[5vw] 2xl:text-[80px]"
          stagger={0.12}
        />
      )}

      <div className="relative mt-16 border-t border-white/10 pt-12 md:mt-20">
        <GhostWord dark className="right-[-2vw] top-[40px] text-[19vw] 2xl:text-[300px]">
          Mission
        </GhostWord>
        <div className="text-[11px] font-semibold uppercase tracking-label text-live">Mission</div>
        <MaskedLines
          as="h2"
          lines={mission.jpLines}
          className="mt-4 text-[7vw] font-black leading-[1.2] tracking-display-ja text-paper md:text-[3.8vw] 2xl:text-[58px]"
        />
        <Reveal delay={0.15} className="mt-8 max-w-[42em] space-y-4">
          <p className="text-[16px] font-bold leading-relaxed tracking-display-ja text-paper md:text-[18px]">
            {mission.lead}
          </p>
          {mission.body.map((p) => (
            <p key={p} className="text-[14px] leading-[1.95] text-gray-400 md:text-[15px]">
              {p}
            </p>
          ))}
        </Reveal>
        {aboutLink && (
          <Reveal delay={0.2} className="mt-10">
            <Link
              to="/about"
              className="group inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-label text-paper"
            >
              About SALT2
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        )}
      </div>
    </div>
  );
}
